// FILE: /src/Components/PostUploader/SocialPostGenerator.js (FULLY UPDATED)

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Modal, Spin, message, Input, Select, Button, Checkbox, Tooltip, Image as AntImage, Collapse } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { fetchAssets } from '../../store/Slices/librarySlice';
import { generateSocialPostService } from '../../services/ai';
import { EyeOutlined } from '@ant-design/icons';
import UpgradeModal from '../BuySubscription/UpgradeModal';
import { getOnboardingForGroup } from '../../services/group';

const { TextArea } = Input;
const { Option } = Select;
const ASSETS_PER_PAGE = 15;

// --- Child Component: Placeholder for pending generations ---
const GenerationPlaceholder = () => {
    const [elapsedTime, setElapsedTime] = useState(0);

    useEffect(() => {
        const timer = setInterval(() => {
            setElapsedTime(prev => prev + 1);
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
        const secs = (seconds % 60).toString().padStart(2, '0');
        return `${mins}:${secs}`;
    };

    return (
        <div className="relative aspect-square bg-gray-100 rounded-lg flex flex-col items-center justify-center text-center p-2 border-2 border-dashed border-gray-300">
            <Spin />
            <p className="text-xs text-gray-600 mt-2 font-semibold">Generating...</p>
            <p className="absolute bottom-1 right-2 text-xs text-gray-400 font-mono">{formatTime(elapsedTime)}</p>
        </div>
    );
};

// --- Child Component: Card for a completed generation ---
const GeneratedPostCard = ({ post, onSelect, isSelected }) => (
    <div 
        className={`relative border-2 rounded-lg shadow-sm overflow-hidden bg-white cursor-pointer transition-all ${isSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-transparent hover:border-blue-300'}`}
        onClick={() => onSelect(post)}
    >
        <div className="absolute top-2 right-2 z-10 bg-white rounded-full shadow">
            <Checkbox checked={isSelected} />
        </div>
        <div className="relative">
            {post.imageUrls.length > 1 ? (
                 <AntImage.PreviewGroup>
                    <div className="grid grid-cols-2 gap-0.5">
                        {post.imageUrls.slice(0, 4).map((url, i) => <AntImage key={i} src={url} className="aspect-square object-cover" />)}
                    </div>
                </AntImage.PreviewGroup>
            ) : (
                <AntImage src={post.imageUrls[0]} className="aspect-square object-cover w-full" />
            )}
        </div>
        <div className="p-3">
            {post.caption && (
                <p className="text-xs text-gray-700 whitespace-pre-wrap max-h-24 overflow-y-auto">{post.caption}</p>
            )}
        </div>
    </div>
);


const SocialPostGeneratorModal = ({ isOpen, onClose, onPostGenerated, isDraftMode = false }) => {
    const dispatch = useDispatch();
    const { selectedGroup, groups } = useSelector((state) => state.groups);
    const { assets, status: libraryStatus } = useSelector((state) => state.library);

    const [isLoading, setIsLoading] = useState(false);
    const [pendingGenerations, setPendingGenerations] = useState([]);
    const [completedGenerations, setCompletedGenerations] = useState([]);
    const [selectedForInsertion, setSelectedForInsertion] = useState([]);
    const [libraryPage, setLibraryPage] = useState(1);
    const [upgradeModal, setUpgradeModal] = useState({ visible: false, message: '', featureName: '' });
    const [brandStyles, setBrandStyles] = useState([]);
    const [styleSearchValue, setStyleSearchValue] = useState("");
    
    const [formState, setFormState] = useState({
        prompt: "",
        platform: "instagram",
        post_type: "single",
        visual_style: undefined, // Changed to undefined for clear placeholder
        quality: "mid",
        key_colors: "",
        reference_image_urls: [],
        n: 1,
        language: "English",
        model: "GPT-image-1.5",
    });

    const selectedGroupId = useMemo(() => groups.find(g => g.name === selectedGroup)?._id, [selectedGroup, groups]);

    useEffect(() => {
        if (isOpen && selectedGroupId) {
            dispatch(fetchAssets(selectedGroupId));
            const fetchBrandKitData = async () => {
                try {
                    const onboardingData = await getOnboardingForGroup(selectedGroupId);
                    if (onboardingData) {
                        if (onboardingData.language) {
                            setFormState(prev => ({ ...prev, language: onboardingData.language }));
                        }
                        if (onboardingData.Visual_Style_Guides && Object.keys(onboardingData.Visual_Style_Guides).length > 0) {
                            const styleKeys = Object.keys(onboardingData.Visual_Style_Guides).map(key => key.replace(/_/g, ' '));
                            setBrandStyles(styleKeys);
                            if (styleKeys.length > 0) {
                                // Default to the first style
                                setFormState(prev => ({ ...prev, visual_style: styleKeys[0] }));
                            }
                        } else {
                            setBrandStyles([]);
                        }
                    }
                } catch (error) {
                    console.warn("Could not fetch brand kit data for AI modal:", error);
                    setBrandStyles([]);
                }
            };
            fetchBrandKitData();
        }
    }, [isOpen, selectedGroupId, dispatch]);

    const handleInputChange = (key, value) => {
        setFormState(prev => {
            const newState = { ...prev, [key]: value };
            if (key === 'model' && value === 'GPT-image-1 mini') {
                if (newState.reference_image_urls.length > 0) {
                    message.info("Reference images cleared as they are not supported by the 'mini' model.");
                }
                newState.reference_image_urls = [];
            }
            return newState;
        });
    };
    
    // --- THIS IS THE FIX: New handlers for the Style Select component ---
    const handleStyleChange = (value) => {
        handleInputChange('visual_style', value);
        setStyleSearchValue(value); // Sync search value so custom input is preserved
    };

    const handleStyleSearch = (value) => {
        setStyleSearchValue(value);
    };

    const handleStyleBlur = () => {
        // If the user typed something but didn't select an option, use the typed value
        if (styleSearchValue && !brandStyles.includes(styleSearchValue)) {
            handleInputChange('visual_style', styleSearchValue);
        }
    };
    // --- END OF FIX ---

    const handleImageSelect = useCallback((url, asset) => {
        if (asset.name?.match(/\.(mp4|mov|webm)$/i)) {
            message.error("Only images can be selected as references, not videos.");
            return;
        }

        setFormState(prev => {
            const currentUrls = prev['reference_image_urls'];
            if (currentUrls.includes(url)) {
                return { ...prev, reference_image_urls: currentUrls.filter(u => u !== url) };
            }
            if (currentUrls.length >= 4) {
                message.warning("You can select a maximum of 4 reference images.");
                return prev;
            }
            return { ...prev, reference_image_urls: [...currentUrls, url] };
        });
    }, []);

    const clearReferenceSelection = () => {
        setFormState(prev => ({ ...prev, reference_image_urls: [] }));
    };
    
    const handleGenerate = async () => {
        if (!formState.prompt.trim()) {
            message.warning("Please enter a prompt.");
            return;
        }

        const requestId = uuidv4();
        const newPlaceholders = Array.from({ length: formState.n }, () => ({ id: `${requestId}-${Math.random()}` }));
        
        setPendingGenerations(prev => [...newPlaceholders, ...prev]);
        setIsLoading(true);

        try {
            const payload = { ...formState, groupName: selectedGroup, caption: true };
            const results = await generateSocialPostService(payload);

            if (results.length > 0) {
                const newCompleted = results.map(r => ({ ...r, id: uuidv4(), platform: formState.platform }));
                setCompletedGenerations(prev => [...newCompleted, ...prev]);
                if (selectedGroupId) dispatch(fetchAssets(selectedGroupId));
            } else {
                message.error("The AI could not generate any posts for this prompt.");
            }
        } catch (error) {
            if (error.isLimitError) {
                setUpgradeModal({ visible: true, message: error.message, featureName: error.feature });
            } else {
                message.error(error.message || "Failed to generate post.");
            }
        } finally {
            setPendingGenerations(prev => prev.filter(p => !p.id.startsWith(requestId)));
            setIsLoading(false);
        }
    };

    const handleSelectForInsertion = (postToSelect) => {
        setSelectedForInsertion(prev => {
            const isAlreadySelected = prev.some(p => p.id === postToSelect.id);
            return isAlreadySelected ? prev.filter(p => p.id !== postToSelect.id) : [...prev, postToSelect];
        });
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedForInsertion(completedGenerations);
        } else {
            setSelectedForInsertion([]);
        }
    };
    
    const handleInsert = () => {
        if (selectedForInsertion.length > 0) {
            onPostGenerated(selectedForInsertion);
            handleClose();
        } else {
            message.info("Please select a generated post to insert.");
        }
    };
    
    const handleClose = () => {
        setPendingGenerations([]);
        setCompletedGenerations([]);
        setSelectedForInsertion([]);
        setLibraryPage(1); 
        setFormState({
            prompt: "", platform: "instagram", post_type: "single",
            visual_style: undefined, quality: "mid", key_colors: "", reference_image_urls: [],
            n: 1, language: "English", model: "GPT-image-1.5"
        });
        setBrandStyles([]);
        setStyleSearchValue("");
        onClose();
    };

    const displayedAssets = useMemo(() => {
        return assets.slice(0, libraryPage * ASSETS_PER_PAGE);
    }, [assets, libraryPage]);
    
    return (
        <>
            <Modal
                title={<div className="flex items-center gap-3"><i className="fa-solid fa-robot text-blue-500"></i><span className="font-semibold">AI Social Post Generator</span></div>}
                open={isOpen}
                onCancel={handleClose}
                width={1280}
                footer={[
                    <Button key="close" onClick={handleClose}>Close</Button>,
                    <Button key="insert" type="primary" disabled={selectedForInsertion.length === 0} onClick={handleInsert}>
                        {isDraftMode ? 'Insert as Drafts' : 'Insert to Post'} ({selectedForInsertion.length})
                    </Button>
                ]}
            >
                <div className="grid grid-cols-12 gap-0" style={{ height: '75vh' }}>
                    {/* Left Side: Control Panel */}
                    <div className="col-span-5 bg-slate-50 border-r border-slate-200 overflow-y-auto flex flex-col">
                        <div className="flex-grow p-6 space-y-6">
                            
                            <FormSection title="Describe your vision" icon="fa-lightbulb" description="Describe the main subject and scene for your post.">
                                <FormItem label="Prompt">
                                    <TextArea rows={4} value={formState.prompt} onChange={(e) => handleInputChange('prompt', e.target.value)} placeholder="A majestic black cat with intense yellow eyes..."/>
                                </FormItem>
                            </FormSection>

                            <FormSection title="Define the output" icon="fa-sliders" description="Adjust the visual style, content, and quantity of your posts.">
                                <div className="grid grid-cols-2 gap-4">
                                    <FormItem label="Platform / Size">
                                        <Select value={formState.platform} onChange={(v) => handleInputChange('platform', v)}>
                                            <Option value="instagram">Square</Option>
                                            <Option value="facebook">Horizontal</Option>
                                            <Option value="pinterest">Vertical</Option>
                                        </Select>
                                    </FormItem>
                                    <FormItem label="Number of Posts">
                                        <Select value={formState.n} onChange={(v) => handleInputChange('n', v)}>
                                            <Option value={1}>1</Option><Option value={2}>2</Option><Option value={3}>3</Option>
                                        </Select>
                                    </FormItem>
                                    {/* --- THIS IS THE FIX: Updated Style Select --- */}
                                    <FormItem label="Visual Style">
                                        <Select
                                            showSearch
                                            allowClear
                                            value={formState.visual_style}
                                            onChange={handleStyleChange}
                                            onSearch={handleStyleSearch}
                                            onBlur={handleStyleBlur}
                                            placeholder="Select or type a style"
                                            optionFilterProp="children"
                                            filterOption={(input, option) =>
                                                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                                            }
                                        >
                                            {brandStyles.map(style => (
                                                <Option key={style} value={style}>{style}</Option>
                                            ))}
                                        </Select>
                                    </FormItem>
                                    {/* --- END OF FIX --- */}
                                    <FormItem label="Key Colors">
                                        <Input value={formState.key_colors} onChange={(e) => handleInputChange('key_colors', e.target.value)} placeholder="e.g., dark grey, amber yellow" />
                                    </FormItem>
                                    <FormItem label="Image Quality">
                                        <Select value={formState.quality} onChange={(v) => handleInputChange('quality', v)}>
                                            <Option value="low">Low</Option>
                                            <Option value="mid">Medium</Option>
                                            <Option value="high">High</Option>
                                        </Select>
                                    </FormItem>
                                    <FormItem label="Language">
                                        <Select value={formState.language} onChange={(v) => handleInputChange('language', v)}>
                                            {["English", "Spanish", "French", "German", "Mandarin", "Hindi", "Arabic", "Portuguese", "Russian", "Japanese"].map(lang => (
                                                <Option key={lang} value={lang}>{lang}</Option>
                                            ))}
                                        </Select>
                                    </FormItem>
                                    <FormItem label="Model">
                                        <Select value={formState.model} onChange={(v) => handleInputChange('model', v)}>
                                            <Option value="GPT-image-1">GPT-image-1</Option>
                                            <Option value="GPT-image-1.5">GPT-image-1.5</Option>
                                            <Option value="GPT-image-1 mini">GPT-image-1 mini</Option>
                                        </Select>
                                    </FormItem>
                                </div>
                            </FormSection>
                            
                            <Tooltip title={formState.model === 'GPT-image-1 mini' ? "Reference images are not supported for the 'mini' model." : ""}>
                                <div className={formState.model === 'GPT-image-1 mini' ? 'opacity-50 pointer-events-none' : ''}>
                                    <FormSection title="Add inspiration (Optional)" icon="fa-image" description={`Select up to 4 images from your library. (${formState.reference_image_urls.length}/4)`}>
                                        {formState.reference_image_urls.length > 0 && <Button size="small" type="link" danger onClick={clearReferenceSelection} className="absolute top-4 right-4">Clear</Button>}
                                        <div className="p-2 border rounded-lg bg-gray-100 max-h-40 overflow-y-auto">
                                            {libraryStatus === 'loading' ? <div className="text-center p-4"><Spin /></div> :
                                            assets.length > 0 ? (
                                                <AntImage.PreviewGroup>
                                                    <div className="grid grid-cols-5 gap-2">
                                                        {displayedAssets.map(asset => (
                                                            <ReferenceImage key={asset.url} asset={asset} onSelect={handleImageSelect} isSelected={formState.reference_image_urls.includes(asset.url)} />
                                                        ))}
                                                    </div>
                                                    {assets.length > displayedAssets.length && (<div className="text-center mt-3"><Button size="small" onClick={() => setLibraryPage(p => p + 1)}>Load More</Button></div>)}
                                                </AntImage.PreviewGroup>
                                            ) : <p className="text-center text-gray-500 text-xs py-4">Your media library is empty.</p>}
                                        </div>
                                    </FormSection>
                                </div>
                            </Tooltip>

                        </div>
                        <div className="p-4 border-t border-slate-200 bg-slate-50 sticky bottom-0">
                            <Button type="primary" block size="large" loading={isLoading} onClick={handleGenerate}>
                               <i className="fa-solid fa-wand-magic-sparkles mr-2"></i> Generate
                            </Button>
                        </div>
                    </div>

                    {/* Right Side: Canvas/Results */}
                    <div className="col-span-7 bg-white flex flex-col min-h-0">
                        <div className="flex justify-between items-center p-4 border-b flex-shrink-0">
                            <h3 className="font-semibold text-gray-800">Generated Results</h3>
                            {completedGenerations.length > 0 && (
                                <Checkbox onChange={handleSelectAll} checked={completedGenerations.length > 0 && selectedForInsertion.length === completedGenerations.length} indeterminate={selectedForInsertion.length > 0 && selectedForInsertion.length < completedGenerations.length}>Select All</Checkbox>
                            )}
                        </div>
                        
                        {/* --- START OF FIX: Scrollable container for results --- */}
                        <div className="flex-grow p-4 bg-gray-100 overflow-y-auto min-h-0">
                            {pendingGenerations.length === 0 && completedGenerations.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full text-center text-gray-400">
                                    <i className="fa-regular fa-image text-6xl mb-4"></i>
                                    <p className="font-semibold text-lg text-gray-600">Your creations will appear here</p>
                                    <p className="text-sm">Fill out the form on the left to get started.</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 pb-4">
                                    {pendingGenerations.map(req => <GenerationPlaceholder key={req.id} />)}
                                    {completedGenerations.map((post) => 
                                        <GeneratedPostCard 
                                            key={post.id} 
                                            post={post} 
                                            onSelect={handleSelectForInsertion} 
                                            isSelected={selectedForInsertion.some(p => p.id === post.id)} 
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                        {/* --- END OF FIX --- */}
                    </div>
                </div>
            </Modal>
            
            <UpgradeModal
                isOpen={upgradeModal.visible}
                onClose={() => setUpgradeModal({ visible: false, message: '', featureName: '' })}
                message={upgradeModal.message}
                featureName={upgradeModal.featureName}
            />
        </>
    );
};

const FormSection = ({ title, description, icon, children }) => (
    <div className="relative space-y-3">
        <div className="flex items-center gap-3">
            <i className={`fa-solid ${icon} text-lg text-gray-400`}></i>
            <div>
                <h4 className="font-semibold text-gray-800">{title}</h4>
                <p className="text-xs text-gray-500">{description}</p>
            </div>
        </div>
        {children}
    </div>
);

const FormItem = ({ label, children }) => (
    <div><label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>{children}</div>
);

const ReferenceImage = React.memo(({ asset, onSelect, isSelected }) => (
    <Tooltip title={asset.name}>
        <div className="relative aspect-square group">
             <AntImage 
                src={asset.url}
                wrapperClassName="w-full h-full"
                className={`w-full h-full object-cover rounded-md border-4 transition-all ${isSelected ? 'border-blue-500 scale-105' : 'border-transparent'}`}
                preview={{
                    mask: (
                        <div className="flex items-center justify-center h-full w-full bg-black bg-opacity-40 text-white">
                            <EyeOutlined />
                        </div>
                    ),
                }}
            />
            <div 
                className="absolute inset-0 cursor-pointer"
                onClick={(e) => {
                    e.stopPropagation();
                    onSelect(asset.url, asset);
                }}
            >
                {isSelected && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-md">
                        <i className="fa-solid fa-check-circle text-white text-2xl"></i>
                    </div>
                )}
            </div>
        </div>
    </Tooltip>
));

export default SocialPostGeneratorModal;